import Box from '@/common/components/Box'
import { ImageCarousel } from '@/common/components/ImageCarousel'
import { ImageWithPlaceholder } from '@/common/components/ImageWithPlaceholder'
import SmallBadge from '@/common/components/SmallBadge'
import { formatUnit } from '@/common/utils/formatters'
import { ClockIcon, WrenchIcon } from 'lucide-react'
import { DiagnosticActivityData } from '@/features/maintenanceWorkflow/pages/Show/features/forwardings/types/maintenanceForwarding.type'
import { ProposalModificationInput, useProposalModification } from '../../../hooks/useProposalModification'
import ProposalAwareValuesRow from '../ProposalAwareValuesRow'

interface ActivityRowProps {
  executionActivity: DiagnosticActivityData
  proposalModification?: ProposalModificationInput
}

const ActivityRow = ({
  executionActivity,
  proposalModification,
}: ActivityRowProps) => {
  const modification = useProposalModification({
    modification: proposalModification,
    unitPrice: executionActivity.unit_price,
    warrantyInDays: executionActivity.warranty_in_days,
    quantity: executionActivity.quantity,
    totalValue: executionActivity.total_value,
    disabled: true,
  })

  const quantityDisplay =
    executionActivity.unit_for_quantity === 'minutes' ? (
      <SmallBadge
        LeftIcon={ClockIcon}
        text={formatUnit(executionActivity.quantity, 'hours')}
      />
    ) : (
      <SmallBadge
        text={formatUnit(executionActivity.quantity, 'item')}
      />
    )

  return (
    <Box className="p-2 rounded-lg">
      <div className="flex w-full items-start gap-4">
        <ImageCarousel
          files={
            executionActivity.images?.map((image) => ({
              id: image.id,
              file: image?.url,
            })) || []
          }
          Placeholder={
            <ImageWithPlaceholder className="size-20 rounded-full" iconClassName="size-10" placeholderIcon={WrenchIcon} />
          }
          className="size-20"
          showExpandButton
          showRemoveButton={false}
        />

        <div className="flex flex-col gap-1 flex-1">
          <div className="flex items-center gap-3">
            <ProposalAwareValuesRow
              originalUnitPrice={modification.originalUnitPrice}
              displayNewUnitPrice={modification.displayNewUnitPrice}
              unitPricePercentage={modification.unitPricePercentage}
              originalTotalValue={modification.originalTotalValue}
              displayNewTotalValue={modification.displayNewTotalValue}
              totalValuePercentage={modification.totalValuePercentage}
              originalWarranty={modification.originalWarranty}
              displayNewWarranty={modification.displayNewWarranty}
              quantityDisplay={quantityDisplay}
            />
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col gap-2">
              <p className="font-bold text-base">
                {executionActivity?.display_name || '--'}
              </p>

              {executionActivity.technical_professionals?.length > 0 && (
                <p className="text-xs">
                  {executionActivity.technical_professionals?.map(({ display_name }) => display_name).join(', ')}
                </p>
              )}
            </div>
          </div>

          <p className="text-xs">
            {executionActivity.description}
          </p>
        </div>
      </div>
    </Box>
  )
}

export default ActivityRow
